import {
    Dispatch,
    RefObject,
    SetStateAction,
    SyntheticEvent,
    useCallback,
    useEffect,
    useRef,
    useState
} from 'react';
import ReactCrop, {
    centerCrop,
    convertToPixelCrop,
    Crop,
    makeAspectCrop,
    PixelCrop
} from 'react-image-crop';
import { toast } from 'react-toastify';
import imageCompression from 'browser-image-compression';

import * as C from './crop.style';
import { canvasPreview } from './canvasPreview';

import StyledButton from '@components/styled/StyledButton';
import StyledModal from '@components/styled/StyledModal';

type ImageCropModalProps = {
    file: File | null;
    imageCropModal: boolean;
    setImageCropModal: Dispatch<SetStateAction<boolean>>;
    previewUrl: string;
    setMainImage: Dispatch<SetStateAction<File | null>>;
    setMainPreview: Dispatch<SetStateAction<string>>;
    aspect: number;
};

const ImageCropModal = ({
    file,
    imageCropModal,
    setImageCropModal,
    previewUrl,
    setMainImage,
    setMainPreview,
    aspect
}: ImageCropModalProps) => {
    const previewCanvasRef = useRef<HTMLCanvasElement>(
        null
    ) as RefObject<HTMLCanvasElement>;
    const imgRef = useRef<HTMLImageElement>(
        null
    ) as RefObject<HTMLImageElement>;
    const [crop, setCrop] = useState<Crop | undefined>(undefined);
    const [completedCrop, setCompletedCrop] = useState<PixelCrop | undefined>(
        undefined
    );
    const [scale, setScale] = useState(1);
    const [loading, setLoading] = useState(false);

    const handleClose = () => {
        setImageCropModal(false);
    };

    const centerAspectCrop = useCallback(
        (mediaWidth: number, mediaHeight: number, aspect: number) => {
            return centerCrop(
                makeAspectCrop(
                    {
                        unit: '%',
                        width: 90
                    },
                    aspect,
                    mediaWidth,
                    mediaHeight
                ),
                mediaWidth,
                mediaHeight
            );
        },
        []
    );

    const onLoadImage = useCallback(
        (e: SyntheticEvent<HTMLImageElement>) => {
            if (aspect) {
                const { width, height } = e.currentTarget;
                setCrop(centerAspectCrop(width, height, aspect));
            }
        },
        [aspect]
    );

    const getImageResizing = useCallback(async (file: File) => {
        const resizingBlob = await imageCompression(file, {
            maxSizeMB: 10 // TODO: 추후 조정 필요
        });
        return new File([resizingBlob], file.name, { type: file.type });
    }, []);

    const handleCompleteCrop = useCallback(async () => {
        // 크롭 완료하기
        const image = imgRef.current;
        const previewCanvas = previewCanvasRef.current;

        if (!image || !previewCanvas || !completedCrop) {
            // 크롭, 캔버스가, 이미지가 없을 경우
            throw new Error('Crop canvas does not exist');
        }

        // 업로드된 이미지와 비교하여 사이즈 조정
        const scaleX = image.naturalWidth / image.width;
        const scaleY = image.naturalHeight / image.height;

        const offscreen: OffscreenCanvas = new OffscreenCanvas(
            completedCrop.width * scaleX,
            completedCrop.height * scaleY
        );
        const ctx = offscreen.getContext(
            '2d'
        ) as OffscreenCanvasRenderingContext2D;
        if (!ctx) throw new Error('No 2d context');

        ctx.drawImage(
            previewCanvas,
            0,
            0,
            previewCanvas.width,
            previewCanvas.height,
            0,
            0,
            offscreen.width,
            offscreen.height
        );

        setLoading(true);
        try {
            const fileName = file?.name || 'image';
            const fileType = file?.type || 'image/png';

            const blob = await (offscreen as any).convertToBlob({
                type: 'image/png'
            });
            const newFile = new File([blob], fileName, { type: fileType });
            const resizingFile = await getImageResizing(newFile);
            //TODO: 추후 리졸버 적용
            setMainImage(resizingFile);
            setMainPreview(URL.createObjectURL(resizingFile));
            const { width, height } = imgRef.current;
            const newCrop = centerAspectCrop(width, height, aspect);
            setCrop(newCrop);
            setCompletedCrop(convertToPixelCrop(newCrop, width, height));
            toast.info('이미지 크롭을 완료했습니다.');
            setImageCropModal(false);
        } catch (err) {
            console.error(err);
            toast.error('이미지 처리 중 오류가 발생했습니다.');
        } finally {
            setLoading(false);
        }
    }, [file, completedCrop, imgRef, previewCanvasRef, aspect]);

    useEffect(() => {
        const setCanvasPreview = async () => {
            // 캔버스 세팅
            if (
                completedCrop?.width &&
                completedCrop?.height &&
                imgRef.current &&
                previewCanvasRef.current
            ) {
                await canvasPreview(
                    // 캔버스에 이미지 렌더링
                    imgRef.current,
                    previewCanvasRef.current,
                    completedCrop,
                    scale
                );
            }
        };
        const timeout = setTimeout(setCanvasPreview, 100);
        return () => clearTimeout(timeout);
    }, [completedCrop, imgRef, previewCanvasRef, scale]);

    return (
        <StyledModal
            title="이미지 크롭"
            width="600px"
            isOpen={imageCropModal}
            closeBtnHandler={handleClose}
            marginBottom={32}
        >
            <C.ButtonBox>
                <C.ScaleWrapper>
                    <C.TitleBox>
                        <C.ScaleTitle>비율 조정</C.ScaleTitle>
                        <C.ScaleValue>{scale}</C.ScaleValue>
                    </C.TitleBox>
                    <C.SliderBox>
                        <C.SliderRail />
                        <C.ScaleSlider
                            type="range"
                            min={0.1}
                            max={2}
                            step={0.1}
                            value={scale}
                            disabled={!file}
                            onChange={(e) => setScale(Number(e.target.value))}
                        />
                    </C.SliderBox>
                </C.ScaleWrapper>
                <StyledButton
                    title="이미지 크롭 완료하기"
                    onClick={handleCompleteCrop}
                    // bgColor={theme.colors.positiveBlue}
                />
            </C.ButtonBox>
            <C.ImageCrop>
                <ReactCrop
                    crop={crop}
                    onChange={(_, percentCrop) => setCrop(percentCrop)}
                    onComplete={(crop) => setCompletedCrop(crop)}
                    aspect={aspect}
                    minWidth={30}
                    minHeight={20}
                    maxWidth={imgRef.current?.width}
                    maxHeight={imgRef.current?.height}
                >
                    <C.Image
                        alt="cropImage"
                        ref={imgRef}
                        src={previewUrl}
                        $scale={scale}
                        onLoad={onLoadImage}
                    />
                </ReactCrop>
                <C.Canvas
                    // $width={completedCrop?.width || 440}
                    // $height={completedCrop?.height || 200}
                    ref={previewCanvasRef}
                />
            </C.ImageCrop>
        </StyledModal>
    );
};

export default ImageCropModal;
